import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
// import KamokuSelect from './KamokuSelect';

// Interfaces
interface Kamoku {
    id: number;
    kamoku_code: string;
    kamoku: string;
}

interface HojoKamoku {
    id: number;
    hojo_code: string;
    hojo_name: string;
    kamoku_id: number;
}

interface Transaction {
    id: number; // denpyou_id
    date: string;
    contra_kamoku_id: number | null;
    contra_kamoku_name: string;
    contra_hojo_id: number | null;
    contra_hojo_name: string;
    deposit_amount: number; // 入金
    withdrawal_amount: number; // 出金
    description: string;
    balance?: number; // 残高 (Front-end calculated or fetched)
}

interface CashBankLedgerProps {
    onMenuSelect: (menuId: string) => void;
    tenantName: string;
    currentUser: any;
    apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    currentTenantId: number | null;
}

const CashBankLedger: React.FC<CashBankLedgerProps> = ({
    onMenuSelect,
    tenantName,
    currentUser,
    apiFetch,
    currentTenantId
}) => {
    // State
    const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
    const [currentPeriod, setCurrentPeriod] = useState<string>(new Date().toISOString().slice(0, 7)); // YYYY-MM
    const [kamokuList, setKamokuList] = useState<Kamoku[]>([]);
    const [hojoList, setHojoList] = useState<HojoKamoku[]>([]);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Filtered Kamoku List (Cash/Bank only)
    // For now, we might show all, or filter by code range if known.
    // Assuming 11xx range is cash/bank for standard JP accounting, but let's just allow all for flexibility first
    // or filter client-side.
    const cashBankAccounts = useMemo(() => {
        // Simple heuristic: "現金" or "預金" in name, or specific codes
        return kamokuList.filter(k =>
            k.kamoku.includes('現金') ||
            k.kamoku.includes('預金') ||
            (parseInt(k.kamoku_code) >= 1100 && parseInt(k.kamoku_code) <= 1199)
        );
    }, [kamokuList]);

    // Fetch Masters
    useEffect(() => {
        if (!currentTenantId) return;
        const fetchMasters = async () => {
            try {
                const [kRes, hRes] = await Promise.all([
                    apiFetch('/api/kamoku'),
                    apiFetch('/api/hojo-kamoku')
                ]);
                if (kRes.ok) setKamokuList(await kRes.json());
                if (hRes.ok) setHojoList(await hRes.json());
            } catch (e) {
                console.error(e);
                setError('マスタデータの取得に失敗しました');
            }
        };
        fetchMasters();
    }, [currentTenantId, apiFetch]);

    // Fetch Transactions
    const fetchTransactions = useCallback(async () => {
        if (!currentTenantId || !selectedAccountId) return;

        setLoading(true);
        setError(null);
        try {
            const [year, month] = currentPeriod.split('-');
            const startDate = `${currentPeriod}-01`;
            const endDate = new Date(parseInt(year), parseInt(month), 0).toISOString().slice(0, 10); // End of month

            const params = new URLSearchParams({
                kamoku_id: selectedAccountId.toString(),
                date_from: startDate,
                date_to: endDate
            });

            const response = await apiFetch(`/api/ledgers?${params.toString()}`);
            if (!response.ok) throw new Error('データの取得に失敗しました');

            const data = await response.json();

            // Map API response to Component State
            // We might need to handle previous balance visualization. 
            // For now, we will add a "carry forward" line at the beginning if previous_balance exists.

            const fetchedTransactions: Transaction[] = data.transactions.map((item: any) => ({
                id: item.id,
                date: item.date,
                contra_kamoku_id: null, // Not returned by new API yet, but strictly not needed for display
                contra_kamoku_name: item.contra_kamoku_name || '',
                contra_hojo_id: null,
                contra_hojo_name: '',
                deposit_amount: item.deposit_amount,
                withdrawal_amount: item.withdrawal_amount,
                description: item.description || '',
                balance: item.balance
            }));

            // Optional: Insert a virtual "previous balance" transaction for display
            const previousBalanceTx: Transaction = {
                id: -1, // Virtual ID
                date: startDate, // Start of period
                contra_kamoku_id: null,
                contra_kamoku_name: '前月繰越',
                contra_hojo_id: null,
                contra_hojo_name: '',
                deposit_amount: 0,
                withdrawal_amount: 0,
                description: '前月からの繰越',
                balance: data.previous_balance
            };

            setTransactions([previousBalanceTx, ...fetchedTransactions]);

        } catch (err: any) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, currentTenantId, selectedAccountId, currentPeriod]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);


    // Styles (Money Forward style: clean, grid-like)
    const headerStyle: React.CSSProperties = {
        padding: '16px',
        backgroundColor: '#fff',
        borderBottom: '1px solid #ddd',
        display: 'flex',
        gap: '20px',
        alignItems: 'center'
    };

    const tableHeaderStyle: React.CSSProperties = {
        backgroundColor: '#f5f5f5',
        padding: '8px',
        borderBottom: '1px solid #ddd',
        fontSize: '12px',
        fontWeight: 'normal',
        color: '#666',
        textAlign: 'left'
    };

    const cellStyle: React.CSSProperties = {
        padding: '8px',
        borderBottom: '1px solid #eee',
        fontSize: '13px'
    };

    return (
        <div style={{ backgroundColor: '#f9f9f9', minHeight: '100vh', fontFamily: 'sans-serif' }}>
            <div
                style={{
                    backgroundColor: '#00ba3f', // Money Forward Green-ish
                    color: 'white',
                    padding: '10px 18px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>現預金出納帳</div>
                <button onClick={() => onMenuSelect('main-menu')} style={{ background: 'transparent', border: '1px solid white', color: 'white', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer' }}>
                    メインメニューへ
                </button>
            </div>

            <div style={headerStyle}>
                <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>勘定科目（口座）</label>
                    <select
                        value={selectedAccountId || ''}
                        onChange={(e) => setSelectedAccountId(Number(e.target.value))}
                        style={{ padding: '6px', fontSize: '14px', minWidth: '200px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                        <option value="">選択してください</option>
                        {cashBankAccounts.map(k => (
                            <option key={k.id} value={k.id}>{k.kamoku}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>表示月</label>
                    <input
                        type="month"
                        value={currentPeriod}
                        onChange={(e) => setCurrentPeriod(e.target.value)}
                        style={{ padding: '5px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                </div>
            </div>

            <div style={{ padding: '16px' }}>
                {error && <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}

                <div style={{ backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ ...tableHeaderStyle, width: '100px' }}>日付</th>
                                <th style={{ ...tableHeaderStyle, width: '200px' }}>相手勘定科目</th>
                                <th style={{ ...tableHeaderStyle, width: '120px' }}>入金</th>
                                <th style={{ ...tableHeaderStyle, width: '120px' }}>出金</th>
                                <th style={tableHeaderStyle}>摘要</th>
                                {/* <th style={{...tableHeaderStyle, width: '60px'}}>操作</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Input Row (Always visible at top or bottom? MF usually has it at bottom or distinct area. Let's put at top for quick access or empty row at bottom) */}
                            {/* For this verification version, I will implement viewing first, then adding. */}

                            {transactions.map(tx => (
                                <tr key={tx.id} style={{ backgroundColor: '#fff' }}>
                                    <td style={cellStyle}>{tx.date.slice(0, 10)}</td>
                                    <td style={cellStyle}>{tx.contra_kamoku_name}</td>
                                    <td style={{ ...cellStyle, textAlign: 'right', color: '#007bff' }}>{tx.deposit_amount > 0 ? tx.deposit_amount.toLocaleString() : ''}</td>
                                    <td style={{ ...cellStyle, textAlign: 'right', color: '#d9534f' }}>{tx.withdrawal_amount > 0 ? tx.withdrawal_amount.toLocaleString() : ''}</td>
                                    <td style={cellStyle}>{tx.description}</td>
                                </tr>
                            ))}

                            {transactions.length === 0 && (
                                <tr>
                                    <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                        データが表示されません。科目を選択するか、取引を入力してください。
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CashBankLedger;
